import React from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import { Space, Tooltip, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LockFilled,
} from '@ant-design/icons';
import { ReactComponent as IconAttachment } from 'assets/images/icons/iconTransactionAttachment.svg';
import { ReactComponent as IconTransactionHasAttachment } from 'assets/images/icons/iconTransactionHasAttachment.svg';
import { ReactComponent as IconComment } from 'assets/images/icons/IconTransactionComment.svg';
import { ReactComponent as InfoMessageIcon } from 'assets/images/icons/infoMessageIcon.svg';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { DataJournal } from 'store/vat/models/vatReportData';
import { ExceptionsActionType } from 'store/vat/models';
import { SortIconProps } from '../types/transaction.types';
import { formatDate, formatAmount } from '../utils/transaction.utils';
import {
  QboSourceType,
  XeroSourceType,
  ZohoSourceType,
} from 'store/api/enums';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { getAuthUserLicence } from 'store/auth/selectors';
import { getVatReportTransactionProviderLink } from 'utils/get-provider-link';
import { isSmallPlanLocked } from 'utils/subscriptionPlanGate';

const { Text } = Typography;

interface AttachmentIconWithLockProps {
  isViewing: boolean;
  isAttachmentEnabled: boolean;
  record: DataJournal;
  onAttachmentClick: (record: DataJournal) => void;
  currentSubscription?: CurrentSubscription;
  onUpgradeModalOpen: () => void;
}

const AttachmentIconWithLock: React.FC<AttachmentIconWithLockProps> = ({
  isViewing,
  isAttachmentEnabled,
  record,
  onAttachmentClick,
  currentSubscription,
  onUpgradeModalOpen,
}) => {
  const t = i18n.t.bind(i18n);
  const userLicence = useSelector(getAuthUserLicence);
  const isClickable = !isViewing && isAttachmentEnabled;
  const isSmallPlan = isSmallPlanLocked(currentSubscription, userLicence);

  const icon = (
    <div style={{ position: 'relative' }}>
      {record.hasAttachments ? (
        <IconTransactionHasAttachment
          className="transaction-action-icon"
          style={{ width: 25, height: 25 }}
        />
      ) : (
        <IconAttachment
          className="transaction-action-icon"
          style={{ width: 25, height: 25 }}
        />
      )}
      {isSmallPlan && (
        <LockFilled
          style={{
            display: 'block',
            position: 'absolute',
            top: 12,
            left: 13,
          }}
        />
      )}
    </div>
  );

  const containerStyle: React.CSSProperties = {
    cursor: isClickable ? 'pointer' : 'not-allowed',
    opacity: isClickable ? 1 : 0.5,
    pointerEvents: isClickable ? 'auto' : 'none',
    display: 'flex',
    alignItems: 'center',
  };

  const handleClick = isClickable
    ? isSmallPlan
      ? onUpgradeModalOpen
      : () => onAttachmentClick(record)
    : undefined;

  if (isViewing) {
    return <div style={containerStyle}>{icon}</div>;
  }

  const tooltipTitle = record.hasAttachments
    ? t('organisation.attachment-tooltip-has')
    : t('organisation.attachment-tooltip-check');

  return (
    <Tooltip title={tooltipTitle}>
      <div style={containerStyle} onClick={handleClick}>
        {icon}
      </div>
    </Tooltip>
  );
};

const ALLOWED_ATTACHMENT_SOURCE_TYPES = {
  xero: [
    'ACCPAY',
    'ACCPAYPAYMENT',
    'ACCREC',
    'RECEIPT',
    'ACCPAYCREDIT',
    'ACCRECCREDIT',
    'ACCRECPAYMENT',
    'APCREDITPAYMENT',
    'ARCREDITPAYMENT',
    'APOVERPAYMENTSOURCEPAYMENT',
    'APPREPAYMENTSOURCEPAYMENT',
    'AROVERPAYMENTSOURCEPAYMENT',
    'ARPREPAYMENTSOURCEPAYMENT',
    'ARPREPAYMENT',
    'APPREPAYMENT',
    'AROVERPAYMENT',
    'APOVERPAYMENT',
    'TRANSFER',
    'CASHPAID', // Spend Money Bank Transaction
    'CASHREC', // Receive Money Bank Transaction
    'CONTACT',
    'ACCOUNT',
    'MANUALJOURNAL',
    'MANJOURNAL',
    'PURCHASEORDER',
    'SALESQUOTE',
    'EXPCLAIM',
    'EXPPAYMENT',
  ],
  quickbooks: [
    'Invoice',
    'Expense',
    'Journal Entry',
    'Cheque',
    'Sales Receipt',
    'Bill',
    'Supplier Credit',
    'Credit Note',
    'Refund',
    'Credit Card Credit',
  ],

  zoho: [
    'invoice',
    'bill',
    'expense',
    'creditnote',
    'vendor_credit',
    'customer_payment',
    'vendor_payment',
    'journal',
    'sales_receipt'
  ],
};
export const renderSortIcon = ({ sortOrder }: SortIconProps) => {
  if (sortOrder === 'ascend') {
    return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  }
  return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
};

const getExceptionType = (exceptionActionType?: string): string | undefined => {
  if (!exceptionActionType) return undefined;
  return exceptionActionType;
};

const getExceptionBackgroundColor = (exceptionActionType?: string): string => {
  if (!exceptionActionType) return 'transparent';

  switch (ExceptionsActionType[exceptionActionType]) {
    case ExceptionsActionType.ADJUSTMENT:
      return '#bbbb42';
    case ExceptionsActionType.CURRENT_RETURN:
      return '#48B9BF';
    default:
      return 'var(--color-surface, #f3f7f8)';
  }
};

const getExceptionTooltipText = (exceptionActionType?: string): string => {
  if (!exceptionActionType) return '';

  switch (ExceptionsActionType[exceptionActionType]) {
    case ExceptionsActionType.ADJUSTMENT:
      return i18n.t('vat-report.adjusted-exception-from-another-submission');
    case ExceptionsActionType.CURRENT_RETURN:
      return i18n.t('vat-report.included-from-exceptions');
    default:
      return '';
  }
};

export const getTransactionColumns = (
  providerName: string,
  onAttachmentClick: (record: DataJournal) => void,
  viewedAttachmentId?: string,
  connectedProperly?: boolean,
  customPagination?: boolean,
  currentSubscription?: CurrentSubscription,
  onUpgradeModalOpen?: () => void,
  currentEntityName?: string,
  locale?: string,
  organisationId?: string,
  providerShortCode?: string
) => {
  const t = i18n.t.bind(i18n);
  const formatAmt = (amount: number | null | undefined) =>
    formatAmount(amount, locale);
  const providerLower = providerName?.toLowerCase();
  let columns = [
    {
      key: 'providerIcon',
      fixed: 'left',
      render: (_: any, record: DataJournal) => {
        const iconProvider =
          record.entityProviderName || providerName;
        const providerHref = getVatReportTransactionProviderLink(
          record,
          providerName,
          organisationId,
          providerShortCode
        );
        const icon = (
          <ProviderIcon
            providerName={iconProvider}
            alt={record.entityProviderName || providerName}
            style={{ marginTop: 7 }}
            width={25}
          />
        );

        return (
          <div
            style={{ display: 'flex', justifyContent: 'center' }}
            data-exception-type={getExceptionType(record.exceptionActionType)}
          >
            <div
              style={{
                width: 6,
                backgroundColor: 'transparent',
                marginLeft: -12,
                marginTop: -4,
                marginBottom: -4,
                marginRight: 10,
                height: 45,
              }}
            >
              <div
                className={record.exceptionActionType ? 'exception-bar' : ''}
                style={{
                  background: getExceptionBackgroundColor(
                    record.exceptionActionType
                  ),
                  height: 45,
                  borderTopLeftRadius: 10,
                  borderBottomLeftRadius: 10,
                }}
              />
            </div>
            {providerHref ? (
              <a
                href={providerHref}
                style={{ cursor: 'pointer' }}
                target="_blank"
                rel="noreferrer"
              >
                {icon}
              </a>
            ) : (
              icon
            )}
          </div>
        );
      },
    },
    {
      title: t('transactions.mapping'),
      dataIndex: 'selectedValue',
      key: 'selectedValue',
      fixed: 'left',
      render: (content: string, record: DataJournal) => (
        <Space
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              minWidth: 100,
              maxWidth: 130,
              fontSize: 12,
            }}
          >
            {content}
          </Text>
          {record.exceptionActionType && (
            <Tooltip
              title={getExceptionTooltipText(record.exceptionActionType)}
              className="tooltip-info"
            >
              <InfoMessageIcon
                style={{ width: 21, height: 21, cursor: 'pointer' }}
                className="testing-icon"
              />
            </Tooltip>
          )}
        </Space>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.selectedValue?.localeCompare(b.selectedValue),
      sortIcon: renderSortIcon,
    },
    {
      key: 'actions',
      title: '',
      fixed: 'left',
      render: (_: any, record: DataJournal) => {
        const isViewing =
          record.sourceId === viewedAttachmentId ||
          record.invoiceId === viewedAttachmentId;
        const provider = providerName?.toLowerCase();
        const allowedTypes =
          ALLOWED_ATTACHMENT_SOURCE_TYPES[provider || ''];

        // Use the same identifier logic as handleAttachmentClick, but fall
        // back to uuid when sourceId/invoiceId are missing (e.g. receipts).
        const transactionIdentifier =
          provider === 'quickbooks'
            ? record.invoiceId || record.sourceId || record.uuid
            : record.sourceId || record.invoiceId || record.uuid;

        const canShowByType =
          provider === 'receipts' || provider === 'excel'
            ? true
            : allowedTypes?.some((type) => type === record.sourceType);

        const showAttachmentIcon = !!(transactionIdentifier && canShowByType);
        const isAttachmentEnabled =
          showAttachmentIcon && connectedProperly === true;

        return (
          <Space
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Tooltip title={t('vat-report.comment-coming-soon')}>
              <IconComment
                className="transaction-action-icon"
                style={{ width: 25, height: 25 }}
              />
            </Tooltip>

            {showAttachmentIcon ? (
              <AttachmentIconWithLock
                isViewing={isViewing}
                isAttachmentEnabled={isAttachmentEnabled}
                record={record}
                onAttachmentClick={onAttachmentClick}
                currentSubscription={currentSubscription}
                onUpgradeModalOpen={onUpgradeModalOpen as () => void}
              />
            ) : (
              <div style={{ width: 16, height: 16 }} />
            )}
          </Space>
        );
      },
    },

    {
      title: t('common.entity'),
      dataIndex: 'entityName',
      key: 'entity',
      ellipsis: true,
      render: (_: any, record: DataJournal) =>
        currentEntityName ?? record.entityName ?? '',
    },
    {
      title: t('organisation.tax-name'),
      dataIndex: 'taxName',
      key: 'taxName',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.taxName?.localeCompare(b.taxName),
      sortIcon: renderSortIcon,
    },
    {
      title: t('vat-report.created-date'),
      dataIndex: 'journalCreatedDate',
      key: 'journalCreatedDate',
      ellipsis: true,
      render: (_: any, record: DataJournal) =>
        formatDate(record.journalCreatedDate),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            new Date(a.journalCreatedDate).valueOf() -
            new Date(b.journalCreatedDate).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('vat-report.journal-date'),
      dataIndex: 'journalDate',
      key: 'journalDate',
      ellipsis: true,
      render: (_: any, record: DataJournal) => formatDate(record.journalDate),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            new Date(a.journalDate).valueOf() -
            new Date(b.journalDate).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('exceptions.sourceType'),
      dataIndex: 'sourceType',
      key: 'sourceType',
      ellipsis: true,
      render: (content: string) => {
        const provider = providerName?.toLowerCase();
        // For Excel / receipts-based data, show the raw sourceType value.
        if (provider === 'excel' || provider === 'receipts') {
          return content;
        }
        if (provider === 'xero') {
          return XeroSourceType[content] ?? content;
        }
        if (provider === 'quickbooks') {
          return QboSourceType[content] ?? content;
        }
        // Default to Zoho mapping, but fall back to raw value if unmapped.
        return ZohoSourceType[content] ?? content;
      },
      sorter: (a: DataJournal, b: DataJournal) =>
        (a.sourceType || '').localeCompare(b.sourceType || ''),
      sortIcon: renderSortIcon,
    },
    // A/C Code is hidden for Excel / Receipts providers (handled via columns
    // filtering below) so it never shows in the UI or customization.
    {
      title: t('organisation.account-name'),
      dataIndex: 'accountCode',
      key: 'accountCode',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.accountCode?.localeCompare(b.accountCode),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.accountType'),
      dataIndex: 'accountType',
      key: 'accountType',
      ellipsis: true,
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.accountType?.localeCompare(b.accountType),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.accountName'),
      dataIndex: 'accountName',
      key: 'accountName',
      ellipsis: true,
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.accountName?.localeCompare(b.accountName),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.contact'),
      dataIndex: 'contact',
      key: 'contact',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.contact || '').localeCompare(b.contact || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.invoice-no'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.invoiceNumber || '').localeCompare(b.invoiceNumber || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      width: 240,
      render: (content: string) => (
        <div
          title={content || ''}
          style={{
            display: 'inline-block',
            width: '100%',
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {content || ''}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.description || '').localeCompare(b.description || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('organisation.reference'),
      dataIndex: 'reference',
      key: 'reference',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.reference || '').localeCompare(b.reference || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('common.currency'),
      dataIndex: 'currency',
      key: 'currency',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.currency || '').localeCompare(b.currency || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.net'),
      dataIndex: 'netAmountConverted',
      key: 'netAmountConverted',
      width: 100,
      fixed: 'right',
      className: 'transactions-amount-column',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.netAmountConverted > b.netAmountConverted ? -1 : 1,
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.tax'),
      dataIndex: 'taxAmountConverted',
      key: 'taxAmountConverted',
      width: 100,
      fixed: 'right',
      className: 'transactions-amount-column',
      render: (content: number, record: DataJournal) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}
          data-exception-type={getExceptionType(record.exceptionActionType)}
        >
          <div
            style={{
              textAlign: 'end',
              color: content < 0 ? '#d13131' : '',
            }}
          >
            {formatAmt(content)}
          </div>
          <div
            style={{
              width: 6,
              backgroundColor: 'transparent',
              marginLeft: 10,
              marginTop: -4,
              marginBottom: -4,
              marginRight: -13,
              height: 45,
              border: 0,
            }}
          >
            <div
              style={{
                background: getExceptionBackgroundColor(
                  record.exceptionActionType
                ),
                height: 45,
                borderTopRightRadius: 10,
                borderBottomRightRadius: 10,
              }}
            />
          </div>
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            a.taxAmountConverted > b.taxAmountConverted ? -1 : 1,
      sortIcon: renderSortIcon,
    },
    // Export-only columns (hidden by default)
    {
      title: t('transactions.source-id'),
      dataIndex: 'sourceId',
      key: 'sourceId',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.sourceId || '').localeCompare(b.sourceId || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('exceptions.netAmount'),
      dataIndex: 'netAmount',
      key: 'netAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.netAmount || 0) - (b.netAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.taxAmount'),
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.taxAmount || 0) - (b.taxAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.modified-net-amount'),
      dataIndex: 'modifiedNetAmount',
      key: 'modifiedNetAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.modifiedNetAmount || 0) - (b.modifiedNetAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.modified-tax-amount'),
      dataIndex: 'modifiedTaxAmount',
      key: 'modifiedTaxAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.modifiedTaxAmount || 0) - (b.modifiedTaxAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.gross-amount'),
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.grossAmount || 0) - (b.grossAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.gross-amount-converted'),
      dataIndex: 'grossAmountConverted',
      key: 'grossAmountConverted',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.grossAmountConverted || 0) - (b.grossAmountConverted || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.journal-number'),
      dataIndex: 'journalNumber',
      key: 'journalNumber',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            String(a.journalNumber ?? '').localeCompare(
              String(b.journalNumber ?? '')
            ),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.payment-status'),
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.paymentStatus || '').localeCompare(b.paymentStatus || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.applied-amount'),
      dataIndex: 'appliedAmount',
      key: 'appliedAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.appliedAmount || 0) - (b.appliedAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.tax-type'),
      dataIndex: 'taxType',
      key: 'taxType',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.taxType || '').localeCompare(b.taxType || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.taxRate'),
      dataIndex: 'taxRate',
      key: 'taxRate',
      render: (content: number) => (content ? `${content}%` : ''),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) => (a.taxRate || 0) - (b.taxRate || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.value-modifier'),
      dataIndex: 'valueModifier',
      key: 'valueModifier',
      render: (content: number) => formatAmt(content),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.valueModifier || 0) - (b.valueModifier || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('common.error'),
      dataIndex: 'error',
      key: 'error',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.error || '').localeCompare(b.error || ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.is-processed'),
      dataIndex: 'isProcessed',
      key: 'isProcessed',
      render: (content: boolean) => (content ? t('common.yes') : t('common.no')),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            Number(a.isProcessed || 0) - Number(b.isProcessed || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.deferred-count'),
      dataIndex: 'deferredCount',
      key: 'deferredCount',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.deferredCount || 0) - (b.deferredCount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.is-reverse-charge'),
      dataIndex: 'isReverseCharge',
      key: 'isReverseCharge',
      render: (content: boolean) => (content ? t('common.yes') : t('common.no')),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            Number(a.isReverseCharge || 0) - Number(b.isReverseCharge || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.reverse-charge-amount'),
      dataIndex: 'reverseChargeAmount',
      key: 'reverseChargeAmount',
      render: (content: number) => (
        <div
          style={{
            textAlign: 'end',
            color: content < 0 ? '#d13131' : '',
          }}
        >
          {formatAmt(content)}
        </div>
      ),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            (a.reverseChargeAmount || 0) - (b.reverseChargeAmount || 0),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.upload-date'),
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (content: string) => (content ? formatDate(content) : ''),
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) =>
            new Date(a.uploadDate || 0).valueOf() -
            new Date(b.uploadDate || 0).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('transactions.sort-id'),
      dataIndex: 'sortId',
      key: 'sortId',
      sorter: customPagination
        ? true
        : (a: DataJournal, b: DataJournal) => (a.sortId || 0) - (b.sortId || 0),
      sortIcon: renderSortIcon,
    },
  ];

  // For Excel / Receipts providers, completely remove the A/C Code column so
  // it can't be displayed or customized for those providers.
  if (providerLower === 'excel' || providerLower === 'receipts') {
    columns = columns.filter((col) => col.key !== 'accountCode');
  }

  if (providerName?.toLocaleLowerCase() === 'zoho') {
    columns = columns.filter(
      (col) =>
        ![
          'accountCode',
          'accountType',
          'accountName',
          'description',
          'reference',
        ].includes(col.key || '')
    );
  }

  return columns;
};
